import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Package, ArrowLeft, Info, ShoppingCart, Check } from 'lucide-react';
import { formatDateUK, formatDateISO, formatCurrencySimple, parseISODate } from '../utils/formatters';
import { toast } from '../hooks/use-toast';
import { useCart } from '../contexts/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Helper function to get proper image URL
const getImageUrl = (equipment) => {
  const img = equipment?.image_url || equipment?.image;
  if (!img) return 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80';
  if (img.startsWith('/api')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('http')) return img;
  return `${BACKEND_URL}${img}`;
};

const EquipmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const inCart = isInCart(id);
  const [selectedDate, setSelectedDate] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [equipment, setEquipment] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleAddToCart = () => {
    if (equipment) {
      addToCart(equipment, quantity);
      toast({
        title: "Added to Cart",
        description: `${equipment.name} has been added to your cart.`,
      });
    }
  };

  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/equipment/${id}`);
      setEquipment(response.data);
    } catch (err) {
      console.error('Failed to fetch equipment:', err);
      setError('Failed to load equipment details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAvailability = useCallback(async () => {
    try {
      const today = new Date();
      const sixMonthsLater = new Date();
      sixMonthsLater.setMonth(today.getMonth() + 6);
      
      const response = await axios.get(
        `${API}/equipment-bookings/availability/${id}?start_date=${formatDateISO(today)}&end_date=${formatDateISO(sixMonthsLater)}`
      );
      
      const bookedDateObjects = response.data.fully_booked_dates?.map(dateStr => parseISODate(dateStr)) || [];
      setBookedDates(bookedDateObjects);
    } catch (err) {
      console.error('Failed to fetch availability:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchEquipment();
    fetchAvailability();
  }, [fetchEquipment, fetchAvailability]);

  const isDateBooked = (date) => {
    return bookedDates.some(bookedDate => 
      bookedDate.getDate() === date.getDate() &&
      bookedDate.getMonth() === date.getMonth() &&
      bookedDate.getFullYear() === date.getFullYear()
    );
  };

  const handleBookNow = () => {
    if (!selectedDate) {
      toast({
        title: "Select a date",
        description: "Please select a date for your booking",
        variant: "destructive"
      });
      return;
    }
    
    if (quantity < 1 || quantity > equipment.quantity_available) {
      toast({
        title: "Invalid quantity",
        description: `Please select between 1 and ${equipment.quantity_available} units`,
        variant: "destructive"
      });
      return;
    }

    navigate(`/equipment-booking/${equipment.equipment_id}`, { 
      state: { selectedDate, quantity } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !equipment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Equipment Not Found</h2>
          <Button onClick={() => navigate('/equipment')} className="bg-yellow-500 hover:bg-yellow-600 text-black">Back to Equipment</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          variant="ghost"
          className="mb-6 text-gray-400 hover:text-white hover:bg-gray-800"
          onClick={() => navigate('/equipment')}
          data-testid="back-to-equipment-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Equipment
        </Button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Image and Details */}
          <div className="space-y-6">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={getImageUrl(equipment)}
                alt={equipment.name}
                className="w-full h-96 object-cover"
              />
              {equipment.category && (
                <div className="absolute top-4 right-4">
                  <Badge className="bg-yellow-500 text-black text-sm py-1 px-3 font-semibold">
                    {equipment.category}
                  </Badge>
                </div>
              )}
              {equipment.quantity_available > 0 && (
                <div className="absolute top-4 left-4">
                  <Badge className="bg-green-500 text-white text-sm py-1 px-3">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {equipment.quantity_available} Available
                  </Badge>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
            </div>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Equipment Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <Package className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Available Units</p>
                    <p className="font-semibold text-white">{equipment.quantity_available}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-yellow-500/20 rounded-lg">
                    <Info className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <p className="font-semibold text-white">{equipment.category || 'General'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Info and Booking */}
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold text-white mb-4" data-testid="equipment-name">{equipment.name}</h1>
              <p className="text-xl text-gray-400 mb-6">
                {equipment.description || 'Professional audio equipment for your events'}
              </p>
              <div className="flex items-baseline mb-8">
                <span className="text-5xl font-bold text-yellow-500">{formatCurrencySimple(equipment.price)}</span>
                <span className="text-xl text-gray-500 ml-3">per day</span>
              </div>
            </div>

            <Separator className="bg-gray-700" />

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Select Quantity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="quantity" className="text-gray-300">How many units do you need?</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    max={equipment.quantity_available}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(Math.max(1, parseInt(e.target.value) || 1), equipment.quantity_available))}
                    className="w-32 bg-gray-700 border-gray-600 text-white"
                    data-testid="quantity-input"
                  />
                  <p className="text-sm text-gray-500">
                    Max: {equipment.quantity_available} units
                  </p>
                </div>
                {quantity > 1 && (
                  <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                    <p className="text-sm font-medium text-yellow-500">
                      Total: {formatCurrencySimple(equipment.price * quantity)} per day for {quantity} units
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Check Availability</CardTitle>
                <p className="text-sm text-gray-500 mt-2">
                  {bookedDates.length > 0 ? (
                    <span className="text-orange-500">
                      {bookedDates.length} date{bookedDates.length !== 1 ? 's' : ''} fully booked
                    </span>
                  ) : (
                    <span className="text-green-500">All dates available</span>
                  )}
                </p>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (date < today) return true;
                    return isDateBooked(date);
                  }}
                  className="rounded-md w-full text-white"
                  modifiers={{
                    booked: bookedDates
                  }}
                  modifiersStyles={{
                    booked: {
                      textDecoration: 'line-through',
                      color: '#ef4444',
                      fontWeight: 'bold'
                    }
                  }}
                />
                {selectedDate && (
                  <p className="mt-4 text-sm text-green-400">
                    Selected date: {formatDateUK(selectedDate)}
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button
                size="lg"
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-lg py-6 transition-all shadow-lg"
                onClick={handleBookNow}
                disabled={equipment.quantity_available < 1}
                data-testid="book-equipment-btn"
              >
                {equipment.quantity_available < 1 ? 'Out of Stock' : 'Book Now'}
              </Button>
              <Button
                size="lg"
                variant={inCart ? "outline" : "default"}
                className={`py-6 ${inCart 
                  ? "border-green-500 text-green-500 hover:bg-green-900/20" 
                  : "bg-gray-700 hover:bg-gray-600 text-white"
                }`}
                onClick={handleAddToCart}
                disabled={inCart || equipment.quantity_available < 1}
                data-testid="add-to-cart-detail-btn"
              >
                {inCart ? <Check className="h-5 w-5 mr-2" /> : <ShoppingCart className="h-5 w-5 mr-2" />}
                {inCart ? 'In Cart' : 'Add to Cart'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetail;
